import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../../Avatar';

interface BreadcrumbCrumb {
    label: string;
    path: string | null;
}

interface PracticeFormHeaderProps {
    isDirty: boolean;
    isSaving: boolean;
    canCreate: boolean;
    onNavigate: (path: string) => void;
    onCancel: () => void;
    onCreate: () => void;
}

// Sibling to communications/CommunicationFormHeader - same breadcrumb/
// UnsavedIndicator/action chrome (px-8 pt-4 pb-2 shrink-0 strip, Cancel +
// black primary button, Avatar). Kept as its own component rather than
// reusing CommunicationFormHeader directly: there is no practiceId yet, so
// the breadcrumb has one fewer crumb (no practice-name segment) and the
// primary action always reads "Create practice".
function Breadcrumb({
    crumbs,
    onNavigate
}: {
    crumbs: BreadcrumbCrumb[];
    onNavigate: (path: string) => void;
}) {
    return (
        <div className="flex items-center gap-1.5 text-[13px] text-gray-500 min-w-0">
            {crumbs.map((crumb, i) => (
                <span key={crumb.label} className="flex items-center gap-1.5 min-w-0">
                    {i > 0 && <ChevronRightIcon className="h-3 w-3 text-gray-300 shrink-0" />}
                    {crumb.path ? (
                        <button
                            type="button"
                            onClick={() => onNavigate(crumb.path as string)}
                            className="hover:text-gray-800 transition-colors truncate"
                        >
                            {crumb.label}
                        </button>
                    ) : (
                        <span className="font-semibold text-gray-700 truncate">{crumb.label}</span>
                    )}
                </span>
            ))}
        </div>
    );
}

function UnsavedIndicator({ isSaving, isDirty }: { isSaving: boolean; isDirty: boolean }) {
    if (isSaving) {
        return (
            <span className="flex items-center gap-2 text-[13px] font-semibold text-gray-500">
                <span className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                Creating…
            </span>
        );
    }
    if (isDirty) {
        return (
            <span className="flex items-center gap-2 text-[13px] font-semibold text-gray-600">
                <span className="w-2 h-2 rounded-full bg-[#FECA42] shrink-0" />
                Unsaved changes
            </span>
        );
    }
    return null;
}

export function PracticeFormHeader({
    isDirty,
    isSaving,
    canCreate,
    onNavigate,
    onCancel,
    onCreate
}: PracticeFormHeaderProps) {
    const crumbs: BreadcrumbCrumb[] = [
        { label: 'Accounts', path: '/accounts/practices' },
        { label: 'Practices', path: '/accounts/practices' },
        { label: 'New practice', path: null }
    ];

    return (
        <div className="flex items-center justify-between px-8 pt-4 pb-2 shrink-0 gap-4">
            <Breadcrumb crumbs={crumbs} onNavigate={onNavigate} />
            <div className="flex items-center gap-4 shrink-0">
                <UnsavedIndicator isSaving={isSaving} isDirty={isDirty} />
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isSaving}
                    className="text-sm font-semibold text-gray-500 hover:text-gray-800 px-3 h-9 disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={onCreate}
                    disabled={!canCreate}
                    className="flex items-center gap-2 bg-black text-white font-semibold px-5 h-9 rounded-lg text-sm hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Create practice
                </button>
                <div className="w-10 h-10">
                    <Avatar />
                </div>
            </div>
        </div>
    );
}
